import { useCallback, useEffect, useRef, useState } from 'react';
import { getUser } from '@frontend/auth-helpers';

const ROOT_STORAGE_KEY = 'air-coachmark-tooltips';
const MAX_SHOW_COUNT = 3;
const MAX_DISMISS_COUNT = 2;

type TooltipCounts = {
  showCount: number;
  dismissCount: number;
};

type CoachmarkTooltipStorage = Record<string, TooltipCounts>;

const readAllStorage = (userId: string): CoachmarkTooltipStorage => {
  try {
    const stored = localStorage.getItem(`${ROOT_STORAGE_KEY}:${userId}`);
    if (!stored) return {};
    return JSON.parse(stored) as CoachmarkTooltipStorage;
  } catch {
    return {};
  }
};

const writeAllStorage = (userId: string, value: CoachmarkTooltipStorage) => {
  try {
    localStorage.setItem(`${ROOT_STORAGE_KEY}:${userId}`, JSON.stringify(value));
  } catch {
    // Ignore write errors (e.g. storage disabled / quota exceeded).
  }
};

const readCounts = (all: CoachmarkTooltipStorage, tooltipKey: string): TooltipCounts => {
  const entry = all[tooltipKey];
  return {
    showCount: entry?.showCount ?? 0,
    dismissCount: entry?.dismissCount ?? 0,
  };
};

/**
 * Controls the lifetime frequency cap for a coachmark tooltip.
 * The tooltip stops showing once it has been shown `MAX_SHOW_COUNT` times or
 * dismissed `MAX_DISMISS_COUNT` times, whichever comes first.
 *
 * All tooltip state is stored under a single localStorage key
 * (`air-coachmark-tooltips:userId`), with each tooltip identified by its
 * `tooltipKey`.
 */
export const useCoachmarkTooltip = (tooltipKey: string, { delayMs }: { delayMs?: number } = {}) => {
  const userId = getUser()?.userID ?? '';

  const [counts, setCounts] = useState<TooltipCounts>(() => {
    if (!userId) return { showCount: MAX_SHOW_COUNT, dismissCount: MAX_DISMISS_COUNT };
    return readCounts(readAllStorage(userId), tooltipKey);
  });

  const wouldShow = counts.showCount < MAX_SHOW_COUNT && counts.dismissCount < MAX_DISMISS_COUNT;
  const [delayElapsed, setDelayElapsed] = useState(!delayMs);

  useEffect(() => {
    setDelayElapsed(!delayMs);
  }, [delayMs]);

  useEffect(() => {
    if (!delayMs || !wouldShow) return;
    setDelayElapsed(false);
    const timeoutId = window.setTimeout(() => {
      setDelayElapsed(true);
    }, delayMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [delayMs, wouldShow]);

  const shouldShow = wouldShow && delayElapsed;

  const hasRecordedShow = useRef(false);

  const recordShown = useCallback(() => {
    if (!userId || hasRecordedShow.current) return;
    hasRecordedShow.current = true;
    setCounts((current) => {
      const next = { ...current, showCount: current.showCount + 1 };
      const all = readAllStorage(userId);
      writeAllStorage(userId, { ...all, [tooltipKey]: next });
      return next;
    });
  }, [userId, tooltipKey]);

  const recordDismiss = useCallback(() => {
    if (!userId) return;
    setCounts((current) => {
      const next = { ...current, dismissCount: current.dismissCount + 1 };
      const all = readAllStorage(userId);
      writeAllStorage(userId, { ...all, [tooltipKey]: next });
      return next;
    });
  }, [userId, tooltipKey]);

  return {
    shouldShow,
    recordShown,
    recordDismiss,
  };
};
